import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { ArrowCircleUp, Info } from '@phosphor-icons/react';
import { useEditAppContext } from 'client/lib/EditApp';
import { trpc, useMutation } from 'client/lib/trpc';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useDeploymentStatusTree } from 'client/state/useDeploymentStatusTree';
import { Button } from 'client/components/Button';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { PendingChangesComponents } from './PendingChangesComponents';

export const PendingChangesCard = () => {
  const editApp = useEditAppContext();
  const deploymentID = useDeploymentClientState((state) => state.document?.id);
  const statusTree = useDeploymentStatusTree(deploymentID);
  const updateStackMutation = useMutation(trpc.deployment.updateStack, {
    onSuccess(data) {
      if (data.status === 'ok') {
        // Reload document after applying changes
        editApp.load();
      }
    },
  });

  if (!statusTree || !statusTree.pendingChanges) return null;

  const { state, components, timeUpdated } = statusTree.pendingChanges;
  const isUpdateAllowed = deploymentID && timeUpdated && state === 'modified' && !updateStackMutation.isLoading;

  const handleUpdateStack = async () => {
    const timeUpdated = statusTree?.pendingChanges.timeUpdated;
    if (!deploymentID) return;
    if (!timeUpdated) return;

    await updateStackMutation.mutateAsync({ deploymentID, timeUpdated });
  };

  if (state !== 'modified') return null;

  return (
    <Stack css={cardStyle}>
      <div css={headerStyle}>
        <Typography variant="h6">
          <ArrowCircleUp weight="bold" /> Pending Changes
        </Typography>
        <Typography variant="body2">
          The local <Tag type="grey">diploi.yaml</Tag> file is different from the current configuration.
        </Typography>
      </div>

      <PendingChangesComponents components={components} />

      <div css={actionsStyle}>
        <Typography variant="body2">
          Changes will be applied immediately <Info weight="bold" />
        </Typography>
        <Button primary loading={updateStackMutation.isLoading} disabled={!isUpdateAllowed} onClick={handleUpdateStack}>
          Apply Changes
        </Button>
        {/* <Button primary loading={updateStackMutation.isLoading} disabled={!isUpdateAllowed}>Review Changes</Button> */}
      </div>
    </Stack>
  );
};

const cardStyle = (theme: Theme) => css`
  padding: 26px;
  border-radius: ${theme.borderRadius}px;
  border: 2px solid ${theme.color.primary[100]};
  background: linear-gradient(
    -5deg,
    ${theme.color.background},
    ${transparentize(theme.type === 'light' ? 0.35 : 0, theme.color.primary[60])} 90%
  );
  box-sizing: border-box;

  // FIXME: This should be outside of this component
  margin-bottom: 32px;
`;

const headerStyle = (theme: Theme) => css`
  > h6 {
    display: flex;
    align-items: center;
    gap: 8px;

    > svg {
      color: ${theme.color.primary[100]};
      translate: 0 -2px;
    }
  }
`;

const actionsStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;

  > p {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;
